import { HStack, Stack } from '@metservicedev/met-ui';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { AviationColorCodes } from '@vona/shared';
import { ActivityStatuses, ActivityStatusText } from '@vona/shared/Activity';
import { DurationTypeText, SimpleDurationTypes, ValueDurationTypes } from '@vona/shared/Duration';
import { Statuses, StatusText } from '@vona/shared/Status';

import { createStyles } from '../tss';
import { FormVona, RawFormVona } from '../types/FormVona';
import { FormDateTimePicker } from './FormDateTimePicker';
import { FormSelect } from './FormSelect';
import { FormTextInput } from './FormTextInput';
import { InfoToolTip } from './InfoToolTip';
import { ValidationContainer } from './ValidationContainer';

const useStyles = createStyles(() => ({
  onsetTypeContainer: {
    minWidth: 120,
    maxWidth: 120,
  },
  durationValue: {
    minWidth: 120,
    maxWidth: 120,
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  },
  durationUnit: {
    '& button': {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  },
  durationUnitForm: {
    marginLeft: -1,
    minWidth: 120,
    maxWidth: 120,
    '& :focus, & :hover, & :is([open],[data-open],[data-state=open])': {
      zIndex: 1,
    },
  },
}));

export const NoticeInputs: React.FC = () => {
  const { classes } = useStyles();
  const form = useFormContext<FormVona>();
  const activityStatus = form.watch('activity.status');
  const isValueDuration = form.watch('duration.type') === 'VALUE';
  const onsetType = form.watch('onset.type');
  return (
    <>
      <FormSelect
        data-testid="select-status"
        fieldPath="status"
        items={Statuses.map((status) => ({ label: StatusText[status], value: status }))}
        label="Status"
        tooltipContent="The operational status of the VONA to send"
      />
      <FormTextInput
        data-testid="input-notice-number"
        fieldPath="noticeNumber"
        label="Notice Number"
        tooltipContent="Unique VONA identifier for the volcano in format: YYYY/N."
      />
      <ValidationContainer hstack>
        <FormSelect
          data-testid="select-previous-color-code"
          fieldPath="previousColorCode"
          items={AviationColorCodes.map((colorCode) => ({ label: colorCode, value: colorCode }))}
          label="Previous Colour Code"
        />
        <FormSelect
          data-testid="select-color-code"
          fieldPath="colorCode"
          items={AviationColorCodes.map((colorCode) => ({ label: colorCode, value: colorCode }))}
          label="Current Colour Code"
        />
        <InfoToolTip
          data-testid="tooltip-color-codes"
          tooltipContent={(
            <Stack gap={1}>
              <span>The colour code of the volcano in its previous VONA.</span>
              <span>The current colour code for the volcano.</span>
            </Stack>
          )}
        />
      </ValidationContainer>
      <FormTextInput
        data-testid="input-svo"
        fieldPath="svo"
        label="State Volcano Observatory (SVO)"
        tooltipContent="The name of the State Volcano Observatory responsible for the volcano."
      />
      <FormSelect<FormVona, 'activity.status'>
        data-testid="select-activity-status"
        dependants={['onset.type', 'duration.type']}
        fieldPath="activity.status"
        items={ActivityStatuses.map((status) => ({ label: ActivityStatusText[status], value: status }))}
        label="Volcano Activity Status"
        tooltipContent="The current status of the volcano.
          If &quot;Last volcanic ash emission...&quot; is selected, also specify the time of the last VA emission."
      />
      {activityStatus === 'LAST_VA_EMISSION' && (
        <FormDateTimePicker
          data-testid="date-picker-last-va-emission"
          fieldPath="activity.lastVaEmission"
          label="Last VA emission occurance (UTC)"
          tooltipContent="The time of the last volcanic ash emission."
        />
      )}
      <ValidationContainer hstack>
        <FormSelect
          data-testid="select-onset-type"
          dependants={['onset.time']}
          fieldPath="onset.type"
          items={[
            { label: 'Known', value: 'KNOWN' },
            { label: 'Unknown', value: 'UNKNOWN' },
            { label: 'Nil', value: 'NIL' },
          ] satisfies { label: string; value: RawFormVona['onset']['type'] }[]}
          label="Onset"
          formControlProps={{ className: classes.onsetTypeContainer }}
        />
        <FormDateTimePicker
          data-testid="date-picker-onset-time"
          fieldPath="onset.time"
          label="Activity onset time (UTC)"
          tooltipContent="The time when volcanic activity started."
          disabled={onsetType !== 'KNOWN'}
        />
      </ValidationContainer>
      <ValidationContainer hstack>
        <FormSelect
          data-testid="select-duration-type"
          fieldPath="duration.type"
          items={[
            ...SimpleDurationTypes.map((item) => ({
              label: DurationTypeText[item],
              value: item,
            })),
            { label: 'Value...', value: 'VALUE' },
          ]}
          label="Duration Type"
        />
        <HStack gap={0} alignItems="start">
          <FormTextInput
            data-testid="input-duration-value"
            disabled={!isValueDuration}
            fieldPath="duration.value"
            label="Duration"
            textInputProps={{ className: classes.durationValue }}
          />
          <FormSelect
            data-testid="select-duration-unit"
            disabled={!isValueDuration}
            fieldPath="duration.unit"
            formControlProps={{ className: classes.durationUnitForm }}
            items={ValueDurationTypes.map((item) => ({
              label: DurationTypeText[item],
              value: item,
            }))}
            label="Units"
            selectProps={{ className: classes.durationUnit }}
          />
        </HStack>
        <InfoToolTip
          data-testid="tooltip-color-codes"
          tooltipContent={(
            <Stack gap={1}>
              <span>Specify the duration of activity (if any; otherwise None).</span>
              <span>If duration is &quot;Value...&quot;, specify the value and units.</span>
            </Stack>
          )}
        />
      </ValidationContainer>
    </>
  );
};
